import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { trpc } from "../lib/trpc";

function nf(n: number | null | undefined, dec = 0): string {
  if (n == null) return "—";
  return new Intl.NumberFormat("tr-TR", { minimumFractionDigits: dec, maximumFractionDigits: dec }).format(n);
}

// yyyy-mm-dd (yerel) yardımcıları — <input type=date> ile uyumlu
function ymdOf(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
function isoToYmd(iso: string | null): string | null {
  if (!iso) return null;
  return ymdOf(new Date(iso));
}
function ymdToStartISO(ymd: string): string {
  const p = ymd.split("-");
  return new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2]), 0, 0, 0, 0).toISOString();
}
function ymdToEndExclusiveISO(ymd: string): string {
  const p = ymd.split("-");
  return new Date(Number(p[0]), Number(p[1]) - 1, Number(p[2]) + 1, 0, 0, 0, 0).toISOString(); // ertesi günün başı (üst sınır hariç)
}
type ShiftLite = { id: string; name: string; startTime: string; endTime: string; sortOrder: number; isActive: boolean };
// resolveShiftId (backend) muadili: İstanbul duvar saatine göre içinde bulunulan vardiya.
function currentShiftIdFrom(shifts: ShiftLite[]): string | null {
  if (shifts.length === 0) return null;
  const parts = new Intl.DateTimeFormat("tr-TR", { timeZone: "Europe/Istanbul", hour: "2-digit", minute: "2-digit", hour12: false }).formatToParts(new Date());
  const hh = Number(parts.find((p) => p.type === "hour")?.value ?? "0");
  const mm = Number(parts.find((p) => p.type === "minute")?.value ?? "0");
  const t = (hh % 24) * 60 + mm;
  const toMin = (s: string) => { const [h, m] = s.split(":"); return Number(h) * 60 + Number(m); };
  for (const s of [...shifts].sort((a, b) => a.sortOrder - b.sortOrder)) {
    const start = toMin(s.startTime), end = toMin(s.endTime);
    const inRange = start === end ? true : start < end ? t >= start && t < end : t >= start || t < end;
    if (inRange) return s.id;
  }
  return null;
}
function fmtDate(iso: string | null): string {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", timeZone: "Europe/Istanbul" }).format(new Date(iso));
}
function fmtDateTime(iso: string | null): string {
  if (!iso) return "—";
  return new Intl.DateTimeFormat("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric", hour: "2-digit", minute: "2-digit", timeZone: "Europe/Istanbul" }).format(new Date(iso));
}

const KIND_TR: Record<string, string> = { finished: "Mamul", semi: "Yarı Mamul", stock: "Hammadde" };
const SEV_TR: Record<string, string> = { low: "Düşük", medium: "Orta", high: "Yüksek", critical: "Kritik" };
const EQTYPE_TR: Record<string, string> = { uretim_hatti: "Hat", unite: "Ünite", makina: "Makina", uretim_merkezi: "Merkez" };

export function ProductionReportPrintPage() {
  const [sp] = useSearchParams();
  const center = sp.get("center");
  const initFrom = isoToYmd(sp.get("since")) ?? ymdOf(new Date());
  const initTo = initFrom; // URL tek gün gönderir (since=gün başı, until=ertesi gün) → başlangıç=bitiş

  // Künye: merkez seçiliyse o merkezin tesisinden; "Tümü" ise aktif tesisten.
  const profByLoc = trpc.settings.companyProfile.getForLocation.useQuery({ locationId: center ?? "" }, { enabled: !!center, staleTime: 5 * 60_000 });
  const profByActive = trpc.settings.companyProfile.get.useQuery({}, { enabled: !center, staleTime: 5 * 60_000 });
  const wsQ = trpc.settings.workSchedule.get.useQuery(undefined, { staleTime: 5 * 60_000 });
  const shifts = useMemo<ShiftLite[]>(() => (wsQ.data?.shifts ?? []).filter((s) => s.isActive), [wsQ.data]);
  const currentShiftId = useMemo(() => currentShiftIdFrom(shifts), [shifts]);

  // Filtre durumu — VARSAYILAN: bugün · içinde bulunulan vardiya · yalnız mamul.
  const [fromDate, setFromDate] = useState(initFrom);
  const [toDate, setToDate] = useState(initTo);
  const [shiftSel, setShiftSel] = useState<string>("all"); // "all" veya shiftId
  const [includeSemi, setIncludeSemi] = useState(false);
  const [userSel, setUserSel] = useState<string>("all"); // "all" = tüm kullanıcılar (varsayılan)
  const [shiftTouched, setShiftTouched] = useState(false);
  const [ready, setReady] = useState(false); // varsayılan vardiya çözülene kadar sorguyu beklet

  useEffect(() => {
    if (ready) return;
    if (wsQ.isSuccess) { if (!shiftTouched && currentShiftId) setShiftSel(currentShiftId); setReady(true); }
    else if (wsQ.isError) { setReady(true); }
  }, [wsQ.isSuccess, wsQ.isError, currentShiftId, shiftTouched, ready]);

  const effectiveShift = shiftSel === "all" ? null : shiftSel;
  const validRange = fromDate <= toDate;
  const effectiveUser = userSel === "all" ? null : userSel;
  const q = trpc.reports.dailyProduction.useQuery(
    { since: ymdToStartISO(fromDate), until: ymdToEndExclusiveISO(toDate), centerId: center || undefined, shiftId: effectiveShift, includeSemi, createdBy: effectiveUser },
    { enabled: ready && !!fromDate && !!toDate && validRange },
  );
  const d = q.data;
  // Kullanıcı listesi sunucudan FİLTRESİZ gelir (seçim yapılsa da daralmaz). Sorgu YENİLENİRKEN q.data
  // undefined olur → son bilinen listeyi tut, yoksa select bir an boşalır ve seçili kullanıcı yanlışlıkla
  // "bu dönemde üretim yok" etiketi alır (yalnız veri TAZE iken bu karar verilmeli).
  const lastUsers = useRef<NonNullable<typeof d>["users"]>([]);
  if (d?.users) lastUsers.current = d.users;
  const userList = d?.users ?? lastUsers.current;
  // Görülen adları biriktir: tarih/vardiya değişince seçili kullanıcının o dönemde üretimi olmayabilir →
  // listeden düşer. Select BOŞ görünmesin diye adı hatırlayıp sanal seçenek basarız (sunucuya ekstra sorgu
  // YOK → tesis/merkez dışı bir ad sızdırma riski de yok).
  const seenNames = useRef(new Map<string, string>());
  for (const u of userList) seenNames.current.set(u.id, u.name);
  // Karar yalnız TAZE veriyle: yükleme sırasında eski listeye göre "yok" deme.
  const selMissing = userSel !== "all" && !!d && !d.users.some((u) => u.id === userSel);
  const prof = center ? profByLoc.data : profByActive.data;
  const singleDay = fromDate === toDate;

  /**
   * DEVAM BEYANI KAPISI — sunucu, beyanı eksik Üretim Sorumlusu'na `PRECONDITION_FAILED`
   * döndürür (routers/reports.ts). `FORBIDDEN`den AYRI bir kod olması şart: yetkisi
   * olmayan kullanıcıya "devam beyanını doldurun" demek yanlış yönlendirme olurdu.
   */
  const beyanEksik = q.error?.data?.code === "PRECONDITION_FAILED";

  // Raporu hazırlayan (giriş yapan kullanıcı) — header künye satırında gösterilir.
  const meQ = trpc.auth.me.useQuery(undefined, { staleTime: 5 * 60_000 });
  const meUser = meQ.data?.user as { firstName?: string; lastName?: string; email?: string } | undefined;
  const meName = meUser ? (`${meUser.firstName ?? ""} ${meUser.lastName ?? ""}`.trim() || meUser.email || "—") : "—";

  // Paylaşım: raporun "Parti Detayı"na KADARki kısmı (shareRef bölgesi) JPEG'e çevrilir.
  // Mobilde Web Share API ile dosya paylaşılır; desteklenmeyen tarayıcıda JPEG indirilir.
  const shareRef = useRef<HTMLDivElement>(null);
  const [sharing, setSharing] = useState(false);
  const [shareErr, setShareErr] = useState<string | null>(null);

  async function onShare() {
    const node = shareRef.current;
    if (!node || !d) return;
    setSharing(true);
    setShareErr(null);
    try {
      const { default: html2canvas } = await import("html2canvas");
      const shot = await html2canvas(node, { scale: 2, backgroundColor: "#ffffff", useCORS: true, logging: false });
      // Beyaz kenar boşluğu ekle (çıktı daha temiz görünsün) — ekran yerleşimini bozmadan.
      const M = 28;
      const out = document.createElement("canvas");
      out.width = shot.width + M * 2;
      out.height = shot.height + M * 2;
      const cx = out.getContext("2d");
      if (!cx) throw new Error("Canvas oluşturulamadı");
      cx.fillStyle = "#ffffff";
      cx.fillRect(0, 0, out.width, out.height);
      cx.drawImage(shot, M, M);
      const blob = await new Promise<Blob | null>((res) => out.toBlob(res, "image/jpeg", 0.92));
      if (!blob) throw new Error("Görüntü oluşturulamadı");
      const rangeLabel = singleDay ? fromDate : `${fromDate}_${toDate}`;
      const fileName = `uretim-raporu-${rangeLabel}.jpg`;
      const file = new File([blob], fileName, { type: "image/jpeg" });
      const nav = typeof navigator !== "undefined" ? navigator : undefined;
      if (nav?.share && nav.canShare?.({ files: [file] })) {
        await nav.share({ files: [file], title: "Üretim Raporu", text: `Üretim Raporu — ${singleDay ? fmtDate(d.since) : rangeLabel}` });
      } else {
        const url = URL.createObjectURL(blob);
        const a = document.createElement("a");
        a.href = url;
        a.download = fileName;
        document.body.appendChild(a);
        a.click();
        a.remove();
        setTimeout(() => URL.revokeObjectURL(url), 2000);
      }
    } catch (e) {
      const err = e as { name?: string; message?: string };
      if (err?.name !== "AbortError") setShareErr(err?.message ?? "Paylaşım başarısız"); // AbortError = kullanıcı paylaşımı iptal etti
    } finally {
      setSharing(false);
    }
  }

  return (
    <div className="prp-root">
      <style>{`
        .prp-root { --ink:#14253E; --line:#d7dce5; --muted:#5b6472; --bg-soft:#f2f5fa; background:#fff; color:#1a2230; }
        .prp-page { max-width: 800px; margin: 0 auto; padding: 20px 24px 40px; font-family: 'Montserrat', system-ui, sans-serif; font-size: 12px; }
        .prp-num { font-family: 'JetBrains Mono', monospace; font-variant-numeric: tabular-nums; }
        .prp-head { display:flex; flex-direction:column; border-bottom: 2px solid var(--ink); padding-bottom: 10px; margin-bottom: 14px; }
        .prp-head-top { display:flex; align-items:flex-start; justify-content:space-between; }
        .prp-head-meta { display:flex; flex-wrap:wrap; gap: 6px 18px; margin-top: 10px; padding-top: 8px; border-top: 1px solid var(--line); font-size: 11.5px; color: var(--muted); }
        .prp-head-meta .k { color: var(--muted); }
        .prp-head-meta b { color: var(--ink); font-weight: 700; }
        .prp-title { font-size: 20px; font-weight: 800; color: var(--ink); letter-spacing:-0.01em; }
        .prp-sub { font-size: 11px; color: var(--muted); margin-top: 2px; }
        .prp-coblock { display:flex; align-items:center; gap:12px; }
        .prp-co { text-align:right; font-size: 11px; color: var(--muted); }
        .prp-co b { color: var(--ink); font-size: 13px; }
        .prp-logo { max-height: 52px; max-width: 130px; object-fit: contain; }
        /* Devam beyanı kapısı — engel değil YÖNLENDİRME tonunda: uyarı sarısı, kırmızı değil.
           Kullanıcı bir şeyi bozmadı, bir adımı atlamış. */
        .prp-gate { max-width: 560px; margin: 40px auto; border: 1px solid #e6c88a; border-left: 4px solid #B45309; border-radius: 8px; background: #fdf8ef; padding: 20px 22px; }
        .prp-gate-ttl { font-size: 16px; font-weight: 800; color: #7c4a08; margin-bottom: 8px; }
        .prp-gate-msg { font-size: 13px; line-height: 1.55; color: #3b3226; margin: 0 0 10px; }
        .prp-gate-hint { font-size: 13px; font-weight: 700; color: #7c4a08; margin: 0 0 16px; }
        .prp-gate-actions { display: flex; gap: 8px; flex-wrap: wrap; }
        .prp-gate-actions .prp-btn { text-decoration: none; display: inline-flex; align-items: center; }
        @media print { .prp-gate { border-color: #999; } }
        .prp-kpis { display:grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 16px; }
        .prp-kpi { border:1px solid var(--line); border-radius:6px; padding:8px 10px; background: var(--bg-soft); }
        .prp-kpi .l { font-size: 9.5px; font-weight:700; letter-spacing:.04em; text-transform:uppercase; color: var(--muted); }
        .prp-kpi .v { font-size: 18px; font-weight:800; color: var(--ink); }
        .prp-kpi .u { font-size: 10px; font-weight:600; color: var(--muted); margin-left:3px; }
        .prp-sec { font-size: 13px; font-weight:700; color: var(--ink); margin: 16px 0 6px; padding-bottom:3px; border-bottom:1px solid var(--line); }
        table.prp-t { width:100%; border-collapse: collapse; font-size: 11px; }
        table.prp-t th { text-align:left; font-size: 9.5px; font-weight:700; letter-spacing:.03em; text-transform:uppercase; color: var(--muted); border-bottom:1.5px solid var(--line); padding:5px 6px; }
        table.prp-t td { padding:4px 6px; border-bottom:1px solid #eef1f6; }
        table.prp-t td.n, table.prp-t th.n { text-align:right; font-family:'JetBrains Mono', monospace; font-variant-numeric: tabular-nums; }
        table.prp-t tfoot td { font-weight:800; border-top:1.5px solid var(--ink); border-bottom:none; color: var(--ink); }
        .prp-code { font-family:'JetBrains Mono', monospace; font-size: 9.5px; color: var(--muted); display:block; }
        table.prp-att td { padding:3px 6px; font-size:10.5px; }
        table.prp-att td.p2 { border-left:1px solid var(--line); padding-left:14px; }
        table.prp-att .vb { font-size:8.5px; color:var(--muted); font-weight:600; margin-left:5px; }
        .prp-toolbar { position: sticky; top: 0; z-index: 5; background:#fff; display:flex; gap:12px; align-items:flex-end; justify-content:space-between; flex-wrap:wrap; padding: 10px 24px; border-bottom:1px solid var(--line); }
        .prp-filt { display:flex; gap:10px; align-items:flex-end; flex-wrap:wrap; }
        .prp-filt label { display:flex; flex-direction:column; gap:3px; font-size:10px; font-weight:700; letter-spacing:.03em; text-transform:uppercase; color: var(--muted); }
        .prp-filt label.chk { flex-direction:row; align-items:center; gap:6px; text-transform:none; letter-spacing:0; font-size:12px; font-weight:500; color:#1a2230; padding-bottom:6px; }
        .prp-filt input[type=date], .prp-filt select { font-family:inherit; font-size:12.5px; padding:5px 8px; border:1px solid var(--line); border-radius:5px; background:#fff; color:#1a2230; }
        .prp-filt input[type=checkbox] { width:15px; height:15px; }
        .prp-tb-actions { display:flex; gap:8px; align-items:flex-end; padding-bottom:1px; }
        .prp-btn { border:1px solid var(--ink); background: var(--ink); color:#fff; font-weight:600; font-size:13px; padding:7px 16px; border-radius:6px; cursor:pointer; }
        .prp-btn.sec { background:#fff; color: var(--ink); }
        .prp-btn:disabled { opacity:.5; cursor:default; }
        @media print {
          .prp-toolbar { display:none !important; }
          .prp-page { max-width:none; padding:0; }
          table.prp-t { font-size: 10px; }
          .prp-sec, .prp-head { break-after: avoid; }
          tr { break-inside: avoid; }
        }
        @page { size: A4 portrait; margin: 12mm; }
      `}</style>

      <div className="prp-toolbar">
        <div className="prp-filt">
          <label>Başlangıç<input type="date" value={fromDate} max={toDate} onChange={(e) => setFromDate(e.target.value)} /></label>
          <label>Bitiş<input type="date" value={toDate} min={fromDate} onChange={(e) => setToDate(e.target.value)} /></label>
          <label>Vardiya
            <select value={shiftSel} onChange={(e) => { setShiftTouched(true); setShiftSel(e.target.value); }}>
              <option value="all">Tüm Vardiyalar</option>
              {shifts.map((s) => <option key={s.id} value={s.id}>{s.name} ({s.startTime}–{s.endTime})</option>)}
            </select>
          </label>
          <label>Kullanıcı
            <select value={userSel} onChange={(e) => setUserSel(e.target.value)}>
              <option value="all">Tüm Kullanıcılar</option>
              {selMissing ? <option value={userSel}>{seenNames.current.get(userSel) ?? "Seçili kullanıcı"} (bu dönemde üretim yok)</option> : null}
              {userList.map((u) => <option key={u.id} value={u.id}>{u.name} ({u.batches})</option>)}
            </select>
          </label>
          <label className="chk"><input type="checkbox" checked={includeSemi} onChange={(e) => setIncludeSemi(e.target.checked)} />Yarı mamulleri de göster</label>
        </div>
        <div className="prp-tb-actions">
          {shareErr ? <span style={{ color: "#b91c1c", fontSize: 11, alignSelf: "center", maxWidth: 200 }}>{shareErr}</span> : null}
          <button type="button" className="prp-btn sec" onClick={() => window.close()}>Kapat</button>
          <button type="button" className="prp-btn sec" onClick={onShare} disabled={!d || sharing} title="Raporun Parti Detayı'na kadarki kısmını JPEG olarak paylaş">{sharing ? "Hazırlanıyor…" : "Paylaş"}</button>
          <button type="button" className="prp-btn" onClick={() => window.print()} disabled={!d}>Yazdır</button>
        </div>
      </div>

      <div className="prp-page">
        {!validRange ? (
          <div style={{ color: "#b91c1c" }}>Başlangıç tarihi bitişten sonra olamaz.</div>
        ) : !ready || q.isLoading ? (
          <div style={{ color: "var(--muted)" }}>Rapor hazırlanıyor…</div>
        ) : beyanEksik ? (
          /* DEVAM BEYANI KAPISI — sunucu PRECONDITION_FAILED döndürdü (attendance-gate.ts).
             Genel hata metni yerine NE YAPILACAĞINI söyleyen blok: kullanıcı "bir hata var"
             sanıp destek aramasın, eksik vardiyayı görüp beyanı tamamlasın. Sayfa ayrı
             sekmede açıldığı için beyan ekranına doğrudan gidemiyoruz; Üretim sayfasına
             yönlendirip oradaki "Personel Devam Beyanı"nı tarif ediyoruz. */
          <div className="prp-gate" role="alert">
            <div className="prp-gate-ttl">Personel devam beyanı tamamlanmadı</div>
            <p className="prp-gate-msg">{q.error?.message}</p>
            <p className="prp-gate-hint">
              Devam beyanını tamamladıktan sonra bu işleme devam edebilirsiniz.
            </p>
            <div className="prp-gate-actions">
              <a className="prp-btn" href="/production" target="_blank" rel="noopener">Üretim sayfasını aç</a>
              <button type="button" className="prp-btn sec" onClick={() => void q.refetch()}>Beyanı yaptım, tekrar dene</button>
            </div>
          </div>
        ) : q.isError ? (
          <div style={{ color: "#b91c1c" }}>{q.error?.message}</div>
        ) : d ? (
          <>
            {/* Paylaşım bölgesi — "Parti Detayı"na KADAR (dahil değil). Paylaş butonu bunu JPEG'e çevirir. */}
            <div ref={shareRef} className="prp-share">
            <div className="prp-head">
              <div className="prp-head-top">
                <div>
                  <div className="prp-title">Üretim Raporu</div>
                  <div className="prp-sub">
                    {singleDay ? fmtDate(d.since) : `${fmtDate(d.since)} – ${new Intl.DateTimeFormat("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(new Date(d.until).getTime() - 86400000))}`}
                    {" · "}{d.centerName ? d.centerName : "Tüm Üretim Merkezleri"}
                    {" · "}{d.shiftName ? d.shiftName : "Tüm Vardiyalar"}
                    {" · "}{d.includeSemi ? "Mamul + Yarı Mamul" : "Yalnız Mamul"}
                    {d.selectedUserName ? <> · Kullanıcı: <b>{d.selectedUserName}</b></> : null}
                  </div>
                  <div className="prp-sub">Parti bazında · QC-red hariç · rapor: {fmtDateTime(d.generatedAt)}</div>
                </div>
                <div className="prp-coblock">
                  <div className="prp-co">
                    {prof?.name ? <b>{prof.name}</b> : <b>STEPERP</b>}
                    {prof?.address ? <div>{prof.address}</div> : null}
                  </div>
                  {prof?.logoUrl ? <img className="prp-logo" src={prof.logoUrl} alt={prof.name ?? "Logo"} /> : null}
                </div>
              </div>
              {/* En alt künye satırı: Tarih · Vardiya · Kullanıcı (raporu hazırlayan) */}
              <div className="prp-head-meta">
                <span className="k">Tarih: <b>{singleDay ? fmtDate(d.since) : `${fmtDate(d.since)} – ${new Intl.DateTimeFormat("tr-TR", { day: "2-digit", month: "2-digit", year: "numeric" }).format(new Date(new Date(d.until).getTime() - 86400000))}`}</b></span>
                <span className="k">Vardiya: <b>{d.shiftName ? d.shiftName : "Tüm Vardiyalar"}</b></span>
                <span className="k">Kullanıcı: <b>{meName}</b></span>
              </div>
            </div>

            {/* KPI */}
            <div className="prp-kpis">
              <div className="prp-kpi"><div className="l">Mamul (Koli)</div><div className="v prp-num">{nf(d.kpi.koli)}<span className="u">koli</span></div></div>
              <div className="prp-kpi"><div className="l">Mamul (kg)</div><div className="v prp-num">{nf(d.kpi.kg)}<span className="u">kg</span></div></div>
              <div className="prp-kpi"><div className="l">Toplam Adet</div><div className="v prp-num">{nf(d.kpi.adet)}<span className="u">adet</span></div></div>
              <div className="prp-kpi"><div className="l">Toplam Paket</div><div className="v prp-num">{nf(d.kpi.paket)}<span className="u">paket</span></div></div>
              {d.includeSemi ? <div className="prp-kpi"><div className="l">Yarı Mamul (Hamur)</div><div className="v prp-num">{nf(d.kpi.semiKg)}<span className="u">kg</span></div></div> : null}
              <div className="prp-kpi"><div className="l">Üretim Duruşu</div><div className="v prp-num">{nf(d.faults.downtimeMin)}<span className="u">dk</span></div></div>
              {d.attendance.total > 0 ? (
                <>
                  <div className="prp-kpi"><div className="l">Kişi Başı Koli</div><div className="v prp-num">{nf(d.kpi.koli / d.attendance.total, 1)}<span className="u">koli</span></div></div>
                  <div className="prp-kpi"><div className="l">Kişi Başı Kg</div><div className="v prp-num">{nf(d.kpi.kg / d.attendance.total, 1)}<span className="u">kg</span></div></div>
                  <div className="prp-kpi"><div className="l">Kişi Başı Adet</div><div className="v prp-num">{nf(d.kpi.adet / d.attendance.total, 0)}<span className="u">adet</span></div></div>
                </>
              ) : null}
            </div>

            {/* Vardiya */}
            <div className="prp-sec">Vardiya Bazında Üretim</div>
            <table className="prp-t">
              <thead><tr><th>Vardiya</th><th className="n">Parti</th><th className="n">Mamul (koli)</th><th className="n">Mamul (kg)</th>{d.includeSemi ? <th className="n">Hamur (kg)</th> : null}</tr></thead>
              <tbody>{d.byShift.map((s, i) => <tr key={i}><td>{s.shift}</td><td className="n">{nf(s.batches)}</td><td className="n">{nf(s.koli)}</td><td className="n">{nf(s.kg)}</td>{d.includeSemi ? <td className="n">{nf(s.semiKg)}</td> : null}</tr>)}</tbody>
              <tfoot><tr><td>TOPLAM</td><td className="n">{nf(d.kpi.batches)}</td><td className="n">{nf(d.kpi.koli)}</td><td className="n">{nf(d.kpi.kg)}</td>{d.includeSemi ? <td className="n">{nf(d.kpi.semiKg)}</td> : null}</tr></tfoot>
            </table>

            {/* Personel devam beyanı (pozisyon-vardiya) */}
            {d.attendance.rows.length > 0 ? (
              <>
                <div className="prp-sec">Personel Devamı ({nf(d.attendance.total)} kişi)</div>
                <table className="prp-t prp-att">
                  <thead><tr><th>Pozisyon</th><th className="n">Kişi</th><th className="p2">Pozisyon</th><th className="n">Kişi</th></tr></thead>
                  <tbody>{Array.from({ length: Math.ceil(d.attendance.rows.length / 2) }, (_, r) => {
                    const a = d.attendance.rows[r * 2];
                    const b = d.attendance.rows[r * 2 + 1];
                    return (
                      <tr key={r}>
                        <td>{a!.title}{a!.isShiftBased ? <span className="vb">Vardiyalı</span> : null}</td>
                        <td className="n">{nf(a!.headcount)}</td>
                        {b ? <><td className="p2">{b.title}{b.isShiftBased ? <span className="vb">Vardiyalı</span> : null}</td><td className="n">{nf(b.headcount)}</td></> : <><td className="p2" /><td className="n" /></>}
                      </tr>
                    );
                  })}</tbody>
                  <tfoot><tr><td colSpan={3} style={{ textAlign: "right" }}>TOPLAM</td><td className="n">{nf(d.attendance.total)}</td></tr></tfoot>
                </table>
              </>
            ) : null}

            {/* Ürün sınıfı dağılımı */}
            <div className="prp-sec">Ürün Sınıfı Dağılımı (Mamul)</div>
            <table className="prp-t">
              <thead><tr><th>Sınıf</th><th className="n">Ürün</th><th className="n">Koli</th><th className="n">%</th><th className="n">Paket</th><th className="n">Adet</th><th className="n">kg</th></tr></thead>
              <tbody>{d.byType.map((t, i) => <tr key={i}><td>{t.type}</td><td className="n">{nf(t.products)}</td><td className="n">{nf(t.koli)}</td><td className="n">%{nf(t.koliPct, 1)}</td><td className="n">{nf(t.paket)}</td><td className="n">{nf(t.adet)}</td><td className="n">{nf(t.kg)}</td></tr>)}</tbody>
            </table>

            {/* Ürün bazında */}
            <div className="prp-sec">Ürün Bazında Üretim (Mamul)</div>
            <table className="prp-t">
              <thead><tr><th>Ürün</th><th>Sınıf</th><th className="n">Parti</th><th className="n">Koli</th><th className="n">Paket</th><th className="n">Adet</th><th className="n">kg</th><th className="n">Palet</th></tr></thead>
              <tbody>{d.byProduct.map((p, i) => (
                <tr key={i}><td>{p.name}<span className="prp-code">{p.code}</span></td><td>{p.type}</td><td className="n">{nf(p.batches)}</td><td className="n">{nf(p.koli)}</td><td className="n">{nf(p.paket)}</td><td className="n">{nf(p.adet)}</td><td className="n">{nf(p.kg)}</td><td className="n">{nf(p.palet, 1)}</td></tr>
              ))}</tbody>
              {d.byProduct.length > 0 ? <tfoot><tr><td colSpan={3}>TOPLAM</td><td className="n">{nf(d.kpi.koli)}</td><td className="n">{nf(d.kpi.paket)}</td><td className="n">{nf(d.kpi.adet)}</td><td className="n">{nf(d.kpi.kg)}</td><td className="n"></td></tr></tfoot> : null}
            </table>

            {/* Yarı mamul */}
            {d.bySemi.length > 0 ? (
              <>
                <div className="prp-sec">Yarı Mamul Üretimi (Hamur / Mix)</div>
                <table className="prp-t">
                  <thead><tr><th>Yarı Mamul</th><th className="n">Parti</th><th className="n">Miktar (kg)</th></tr></thead>
                  <tbody>{d.bySemi.map((s, i) => <tr key={i}><td>{s.name}<span className="prp-code">{s.code}</span></td><td className="n">{nf(s.batches)}</td><td className="n">{nf(s.kg)}</td></tr>)}</tbody>
                  <tfoot><tr><td>TOPLAM</td><td className="n"></td><td className="n">{nf(d.kpi.semiKg)}</td></tr></tfoot>
                </table>
              </>
            ) : null}

            {/* Sipariş bazında */}
            {d.byOrder.length > 0 ? (
              <>
                <div className="prp-sec">Sipariş Bazında Üretim</div>
                <table className="prp-t">
                  <thead><tr><th>Sipariş</th><th>Müşteri</th><th className="n">Ürün</th><th className="n">Koli</th><th className="n">Adet</th><th className="n">kg</th></tr></thead>
                  <tbody>{d.byOrder.map((o, i) => <tr key={i}><td className="prp-num">{o.order}</td><td>{o.customer}</td><td className="n">{nf(o.products)}</td><td className="n">{nf(o.koli)}</td><td className="n">{nf(o.adet)}</td><td className="n">{nf(o.kg)}</td></tr>)}</tbody>
                </table>
              </>
            ) : null}

            {/* Arıza ve üretim duruşları (hat bazında) */}
            <div className="prp-sec">
              Arıza ve Üretim Duruşları
              {d.faults.total > 0 ? ` (${nf(d.faults.total)} arıza · ${nf(d.faults.stoppedCount)} üretim durduran · ${nf(d.faults.downtimeMin)} dk)` : ""}
            </div>
            {/* Arızalar ekipman olayıdır, üreten kullanıcıya bağlı değil → kullanıcı seçiliyken bunu açıkça belirt. */}
            {d.selectedUserName ? (
              <div style={{ fontSize: 10.5, color: "var(--muted)", padding: "0 0 6px" }}>
                Arızalar ekipman bazlıdır; kullanıcı filtresinden etkilenmez (merkezin tamamı gösterilir).
              </div>
            ) : null}
            {d.faults.total === 0 ? (
              <div style={{ fontSize: 11, color: "var(--muted)", padding: "2px 0 8px" }}>Bu dönem/vardiyada arıza kaydı yok.</div>
            ) : (
              <table className="prp-t">
                <thead><tr><th>Saat</th><th>Arıza Tipi</th><th>Ekipman</th><th>Önem</th><th>Üretim Durdu</th><th className="n">Duruş (dk)</th></tr></thead>
                <tbody>
                  {d.faults.groups.map((g, gi) => (
                    <Fragment key={gi}>
                      <tr>
                        <td colSpan={6} style={{ background: "var(--bg-soft)", fontWeight: 700, fontSize: 10.5, color: "var(--ink)" }}>
                          {g.hatCode ? "HAT · " : ""}{g.hatName}
                          {g.hatCode ? <span className="prp-code" style={{ display: "inline", marginLeft: 6 }}>{g.hatCode}</span> : null}
                        </td>
                      </tr>
                      {g.rows.map((f, fi) => (
                        <tr key={fi}>
                          <td className="prp-num">{f.time}</td>
                          <td>{f.label}</td>
                          <td>{f.equipment}{f.equipmentType ? <span className="prp-code" style={{ display: "inline", marginLeft: 4 }}>{EQTYPE_TR[f.equipmentType] ?? f.equipmentType}</span> : null}</td>
                          <td>{f.severity ? SEV_TR[f.severity] ?? f.severity : "—"}</td>
                          <td>{f.productionStopped ? <span style={{ color: "#b91c1c", fontWeight: 700 }}>Evet</span> : <span style={{ color: "var(--muted)" }}>Hayır</span>}</td>
                          <td className="n">{f.productionStopped ? (f.durationMin != null ? nf(f.durationMin) : "—") : "—"}</td>
                        </tr>
                      ))}
                      <tr>
                        <td colSpan={5} style={{ textAlign: "right", fontStyle: "italic", fontWeight: 700, color: "var(--muted)" }}>
                          Ara Toplam · {g.hatName} ({nf(g.faultCount)} arıza, {nf(g.stoppedCount)} üretim durduran)
                        </td>
                        <td className="n" style={{ fontWeight: 800 }}>{nf(g.downtimeMin)}</td>
                      </tr>
                    </Fragment>
                  ))}
                </tbody>
                <tfoot>
                  <tr>
                    <td colSpan={5}>TOPLAM ÜRETİM DURUŞU ({nf(d.faults.stoppedCount)} üretim durduran arıza)</td>
                    <td className="n">{nf(d.faults.downtimeMin)} dk</td>
                  </tr>
                </tfoot>
              </table>
            )}

            </div>{/* ← paylaşım bölgesi sonu (Parti Detayı ve sonrası paylaşıma dahil değildir) */}

            {/* Parti detayı */}
            <div className="prp-sec">Parti Detayı ({nf(d.batches.length)} parti)</div>
            <table className="prp-t">
              <thead><tr><th>Saat</th><th>Parti</th><th>Ürün</th><th>Tür</th><th>Sipariş</th><th>Vardiya</th><th>Hat</th><th className="n">Miktar</th><th className="n">kg</th><th className="n">Adet</th></tr></thead>
              <tbody>{d.batches.map((b, i) => (
                <tr key={i}>
                  <td className="prp-num">{b.time}</td>
                  <td className="prp-num" style={{ fontSize: 9.5 }}>{b.code}</td>
                  <td>{b.product}<span className="prp-code">{b.productCode}</span></td>
                  <td>{KIND_TR[b.kind] ?? b.kind}</td>
                  <td className="prp-num" style={{ fontSize: 9.5 }}>{b.order ?? "—"}</td>
                  <td>{b.shift}</td>
                  <td className="prp-num" style={{ fontSize: 9.5 }}>{b.location}</td>
                  <td className="n">{nf(b.base, 2)} <span style={{ color: "var(--muted)", fontSize: 9 }}>{b.baseUom}</span></td>
                  <td className="n">{nf(b.kg)}</td>
                  <td className="n">{nf(b.adet)}</td>
                </tr>
              ))}</tbody>
            </table>

            <div style={{ marginTop: 20, fontSize: 9.5, color: "var(--muted)", textAlign: "center", borderTop: "1px solid var(--line)", paddingTop: 8 }}>
              STEPERP Üretim Raporu · {fmtDate(d.since)} · Bu rapor QC-red edilmiş partileri içermez.
            </div>
          </>
        ) : null}
      </div>
    </div>
  );
}
